import React, { useCallback, useEffect, useMemo } from "react";

import cn from "classnames";

import { EntityTypesSupportingFilteringBy } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { ButtonProps } from "@mapmycustomers/ui";

import { StandardFilterButton } from "@app/component/filtering";
import hasIncompleteValue from "@app/component/filtering/utils/hasIncompleteValue";
import GenericView from "@app/component/view/GenericView/GenericView";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { convertFromPlatformFilterModel } from "@app/util/viewModel/convertFromPlatformFilterModel";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import styles from "./CriteriaExtraFilters.module.scss";

interface Props {
  buttonProps?: ButtonProps;
  buttonText?: string;
  className?: string;
  context: string;
  entityType: EntityTypesSupportingFilteringBy;
  hiddenFields?: IField[];
  onChange?: (filter: PlatformFilterModel) => void;
  value?: PlatformFilterModel;
}

const CriteriaExtraFilters: React.FC<Props> = ({
  buttonProps,
  buttonText,
  className,
  context,
  entityType,
  hiddenFields,
  onChange,
  value,
}) => {
  const fieldModel = getFieldModelByEntityType(entityType);

  const [modifiedFilterModel, setModifiedFilterModel] = useStateWithDebouncedListener(
    [
      useDynamicCallback((modifiedFilterModel: FilterModel) => {
        if (!hasIncompleteValue(modifiedFilterModel) && modifiedFilterModel) {
          const platformModel = convertToPlatformFilterModel(modifiedFilterModel, [], fieldModel);
          onChange?.(platformModel);
        }
      }),
      500,
    ],
    (value ? convertFromPlatformFilterModel(value, fieldModel) : {}) as Partial<FilterModel>
  );

  const fetcher = useCallback(
    (payload: { viewState: Partial<ViewState> }) => {
      if (fieldModel && payload.viewState.filter) {
        setModifiedFilterModel(payload.viewState.filter);
      }
    },
    [fieldModel, setModifiedFilterModel]
  );

  const viewState = useMemo(() => {
    return {
      columns: [],
      filter: modifiedFilterModel as FilterModel,
      range: { endRow: 0, startRow: 0 },
      sort: [],
      viewAs: undefined,
    };
  }, [modifiedFilterModel]);

  useEffect(() => {
    setModifiedFilterModel(value ? convertFromPlatformFilterModel(value, fieldModel) : {});
    // To prevent re-renders when value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, fieldModel]);

  return (
    <GenericView
      className={cn(styles.container, className)}
      entityType={entityType}
      fetcher={fetcher}
      fieldModel={fieldModel}
      viewState={viewState}
    >
      <StandardFilterButton
        buttonProps={buttonProps}
        buttonText={buttonText}
        context={context}
        entityType={entityType}
        hiddenFields={hiddenFields}
        hideSavedFilters
        // To put filter popover above dashboard cards edit modal
        zIndex={1000}
      />
    </GenericView>
  );
};

export default CriteriaExtraFilters;
